import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Provider } from 'jotai';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import Layout from './components/Layout';

// Dashboard pages
import Dashboard from './pages/Dashboard';
import UserProfilePage from './pages/UserProfilePage';
import PipelineManagement from './pages/PipelineManagement';
import DataExplorer from './pages/DataExplorer';
import PipelineMapping from './pages/PipelineMapping';
import PatientsPage from './pages/PatientsPage';
import AppointmentsPage from './pages/AppointmentsPage';
import SyncLogs from './pages/SyncLogs';
import ZoneDMTransactions from './pages/ZoneDMTransactions';
import FourDEMRTransactions from './pages/FourDEMRTransactions';
import LeadsTo4D from './pages/LeadsTo4D';
import AdminDashboard from './pages/AdminDashboard';
import AdminAccountsPage from './pages/AdminAccountsPage';
import IntegrationsManagement from './pages/PipelineManagement';
import WebhookLogs from './pages/WebhookLogs';
import QuotesPage from './pages/QuotesPage';
import TimelineRecords from './pages/TimelineRecords';
import LeadNotes from './pages/LeadNotes';

// Public pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ApiDocsPage from './pages/ApiDocsPage';
import DocsPage from './pages/DocsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import DemoPage from './pages/DemoPage';
import IntegrationsPage from './pages/IntegrationsPage';
import OAuthCallback from './pages/OAuthCallback';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import GettingStartedPage from './pages/GettingStartedPage';

const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <AuthProvider>
            <div className="App">
              <Routes>
                {/* Public Routes */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/api" element={<ApiDocsPage />} />
                <Route path="/docs/*" element={<DocsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/demo" element={<DemoPage />} />
                <Route path="/integrations" element={<IntegrationsPage />} />

                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/getting-started" element={<GettingStartedPage />} />

                {/* Protected Dashboard Routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Dashboard />} />
                  <Route path="profile" element={<UserProfilePage />} />
                  <Route path="integrations" element={<PipelineManagement />} />
                  <Route path="oauth-callback" element={<OAuthCallback />} />
                  <Route path="data" element={<DataExplorer />} />
                  <Route path="pipeline" element={<PipelineMapping />} />
                  <Route path="patients" element={<PatientsPage />} />
                  <Route path="appointments" element={<AppointmentsPage />} />
                  <Route path="quotes" element={<QuotesPage />} />
                  <Route path="sync-logs" element={<SyncLogs />} />
                  <Route path="zonedm-transactions" element={<ZoneDMTransactions />} />
                  <Route path="fourdeemr-transactions" element={<FourDEMRTransactions />} />
                  <Route path="webhook-logs" element={<WebhookLogs />} />
                  <Route path="leads-to-4d" element={<LeadsTo4D />} />
                  <Route path="timeline-records" element={<TimelineRecords />} />
                  <Route path="lead-notes" element={<LeadNotes />} />
                </Route>

                {/* Protected Admin Routes (legacy admin or superadmin) */}
                <Route 
                  path="/admin" 
                  element={
                    <ProtectedRoute requireAdmin>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="accounts" element={<AdminAccountsPage />} />
                  <Route path="users" element={<AdminAccountsPage />} />
                  <Route path="integrations" element={<IntegrationsManagement />} />
                </Route>

                {/* Catch-all route */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster 
                position="top-right"
                toastOptions={{
                  duration: 4000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                }}
              />
            </div>
          </AuthProvider>
        </BrowserRouter>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
